/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import * as WebBrowser from "expo-web-browser";
import React from "react";
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  Text,
  ViewStyle,
} from "react-native";
import { Icon } from "react-native-elements";
// import {crossResponsive as cr} from '../../helpers';
import { isHTML, limitChar, validEmail, validURL } from "./utils";
import { GridItemV, GridV } from "./components/FlexGridV";
import { FBGridV, FBItemV } from "react-flexbox-grid-rs";
// import {TextHighlighterV} from '../TextHighlighterV';
import { TextVW } from "@vlinder-web/text-vfw-widget-react";
import { ScreenWidth } from "react-native-elements/dist/helpers";
import { camelCase } from "change-case";

const is = require("is_js");
const gjv = require("geojson-validation");

const hitSlop = { top: 20, bottom: 20, left: 20, right: 20 };

interface IDetailInfoV {
  title?: string;
  aboutInfo?: string;
  infoList?: any[];
  contentWrapperStyle?: ViewStyle;
  labelStyle?: TextStyle;
  valueStyle?: TextStyle;
  socialLinks?: any[];
  onListItemPress?: (item: any) => void;
  renderSvgIcon?: (item: any) => void;
  containerStyle?: ViewStyle;
  titleStyle?: TextStyle;
  iconContainerStyle?: ViewStyle;
  highlightStyle?: TextStyle;
  aboutInfoContainerStyle?: ViewStyle;
  aboutInfoStyle?: TextStyle;
  bp?: string;
}

export const DetailInfoV = (props: IDetailInfoV): JSX.Element => {
  const {
    aboutInfo,
    title,
    infoList,
    contentWrapperStyle,
    labelStyle,
    valueStyle,
    socialLinks,
    onListItemPress,
    renderSvgIcon,
    containerStyle,
    titleStyle,
    iconContainerStyle,
    highlightStyle,
    aboutInfoContainerStyle,
    aboutInfoStyle,
    bp,
  } = props;

  const _renderTitle = () => {
    if (title && typeof title === "string" && is.not.empty(title)) {
      return (
        <TextVW
          type={"title3"}
          children={title}
          ellipsizeMode={"tail"}
          numberOfLines={2}
          style={StyleSheet.flatten([styles.titleStyle, titleStyle])}
        />
      );
    }
  };

  const _renderAbout = () => {
    if (aboutInfo && typeof aboutInfo === "string" && is.not.empty(aboutInfo)) {
      return (
        <View
          style={StyleSheet.flatten([
            styles.aboutInfoContainerStyle,
            aboutInfoContainerStyle,
          ])}
        >
          <div
            dangerouslySetInnerHTML={{ __html: aboutInfo }}
            style={StyleSheet.flatten([styles.aboutInfoStyle, aboutInfoStyle])}
          />
          {/* <TextVW 
                        children={aboutInfo}
                        //ellipsizeMode={"tail"}
                        // numberOfLines={3}
                        type={"caption"}
                        style={StyleSheet.flatten([
                            styles.aboutInfoStyle,
                            aboutInfoStyle,
                        ])}
                    /> */}
        </View>
      );
    }
  };

  const _onListItemPress = async (item: any) => {
    if (validURL(item?.value) === true) {
      try {
        await WebBrowser.openBrowserAsync(item?.value);
      } catch (err) {
        console.log("err in opening link2", err);
      }
    } else if (
      (item?.label).toLowerCase() === "telephone" ||
      (item?.label).toLowerCase() === "contact" ||
      (item?.label).toLowerCase() === "phone" ||
      (item?.label).toLowerCase() === "mobile"
    ) {
      const phone = item?.value;
      const phoneOpen =
        Platform.OS == "ios" ? `telprompt:${phone}` : `tel:${phone}`;
      Linking.canOpenURL(phoneOpen)
        .then((supported) => {
          if (supported) {
            return Linking.openURL(phoneOpen).catch((err) => console.log(err));
          }
        })
        .catch((err) => console.log(err));
    } else if (validEmail(item?.value) === true) {
      Linking.openURL(`mailto:${item?.value}`);
    } else if (
      item &&
      item.value &&
      is.not.string(item.value) &&
      gjv.valid(item.value)
    ) {
      onListItemPress && onListItemPress(item);
    } else if (item && is.string(item.value) && item.value.length >= 99) {
      onListItemPress && onListItemPress(item);
    } else {
      return;
    }
  };

  const _renderValueText = (value: string) => {
    let _value: string = value && value;
    if (_value && isHTML(_value) === true) {
      return `${"ℹ"}` ?? `${"ⓘ"}`;
    } else if (_value && _value.length <= 130) {
      return _value;
    } else if (_value && _value.length > 130 && isHTML(_value) === false) {
      return (
        `${limitChar(_value, 130)} ${"ℹ"}` ?? `${limitChar(_value, 130)} ${"ⓘ"}`
      );
    } else {
      return _value;
    }
  };

  const _renderValue = (label: string, value: any, key: string) => {
    if (value && is.empty(value)) {
      return;
    }

    if (value && is.not.string(value) && gjv.valid(value)) {
      return (
        <TextVW
          type={"headline"}
          align={"right"}
          numberOfLines={5}
          children={`${"📍"}`}
          style={StyleSheet.flatten([
            styles.valueStyle,
            valueStyle && valueStyle,
          ])}
        />
      );
    }

    if (value && is.string(value) && is.not.empty(value) && validURL(value)) {
      if (key === "credentialUrl") {
        return (
          <>
            <a
              href={value}
              style={{
                textDecorationLine: "underline",
                fontSize: 13,
                fontWeight: "400",
                paddingRight: 8,
                textAlign: "right",
              }}
              target="_blank"
            >
              Click here
            </a>
          </>
        );
      } else {
        return (
          <TextVW
            type={"headline"}
            align={"right"}
            children={value}
            numberOfLines={5}
            style={StyleSheet.flatten([
              styles.valueStyle,
              valueStyle && valueStyle,
              {
                color: "#000",
                textDecorationLine: "underline",
              },
            ])}
          />
        );
      }
    }

    if (value && is.string(value) && is.not.empty(value)) {
      return (
        <TextVW
          type={"headline"}
          align={"right"}
          children={_renderValueText(value)}
          numberOfLines={5}
          style={StyleSheet.flatten([
            styles.valueStyle,
            valueStyle && valueStyle,
          ])}
        />
      );
    }
  };

  const _renderList = () => {
    const data: any = [];
    if (
      infoList &&
      is.array(infoList) &&
      is.not.empty(infoList) &&
      infoList.length > 0
    ) {
      for (let i = 0; i < infoList.length; ++i) {
        if (
          (bp === "xs" || bp === "sm") &&
          camelCase(infoList[i].label) !== "credentialLogo"
        ) {
          data.push(
            <TouchableOpacity
              key={`details-info-list-${i}`}
              style={StyleSheet.flatten([
                styles.contentWrapper,
                contentWrapperStyle && contentWrapperStyle,
              ])}
              onPress={() => _onListItemPress(infoList[i])}
            >
              <GridItemV colSpan={ScreenWidth * 0.4}>
                <View style={{ flexDirection: "row" }}>
                  {renderSvgIcon && renderSvgIcon(infoList[i])}
                  <TextVW
                    type={"headline"}
                    align={"left"}
                    children={`${infoList[i].label}`}
                    style={StyleSheet.flatten([
                      styles.labelStyle,
                      labelStyle && labelStyle,
                    ])}
                  />
                </View>
              </GridItemV>
              <GridItemV colSpan={ScreenWidth * 0.5}>
                {_renderValue(
                  infoList[i].label,
                  infoList[i].value,
                  infoList[i].key ?? ""
                )}
              </GridItemV>
            </TouchableOpacity>
          );
        } else if (camelCase(infoList[i].label) !== "credentialLogo") {
          data.push(
            <TouchableOpacity
              key={`details-info-list-${i}`}
              style={StyleSheet.flatten([
                styles.contentWrapper,
                contentWrapperStyle && contentWrapperStyle,
              ])}
              onPress={() => _onListItemPress(infoList[i])}
            >
              <FBItemV colspan={12}>
                <View style={{ flexDirection: "row" }}>
                  {renderSvgIcon && renderSvgIcon(infoList[i])}
                  <TextVW
                    type={"headline"}
                    align={"right"}
                    children={`${infoList[i].label}`}
                    style={StyleSheet.flatten([
                      styles.labelStyle,
                      labelStyle && labelStyle,
                    ])}
                  />
                </View>
              </FBItemV>
              <FBItemV colspan={12}>
                {_renderValue(
                  infoList[i].label,
                  infoList[i].value,
                  infoList[i].key ?? ""
                )}
              </FBItemV>
            </TouchableOpacity>
          );
        }
      }
      if (bp === "xs" || bp === "sm") {
        return data;
      } else {
        return <FBGridV>{data}</FBGridV>;
      }
    }
  };

  const _openLink = async (URL: string) => {
    try {
      await WebBrowser.openBrowserAsync(URL);
    } catch (err) {
      console.log("open url err", err);
    }
  };

  const _renderSocialIcons = () => {
    const data: any = [];
    if (
      socialLinks &&
      is.array(socialLinks) &&
      is.not.empty(socialLinks) &&
      socialLinks.length > 0
    ) {
      for (let i = 0; i < socialLinks.length; ++i) {
        console.log(`socialLinks ::`, socialLinks[i]);
        let type = socialLinks[i]?.type && socialLinks[i]?.type.split(`-`);
        type = type ? type[1] && type[1].trim() : "";
        console.log(`socialLinks type :: `, type);
        data.push(
          <View
            key={`social-link-${i}`}
            style={StyleSheet.flatten([
              // styles.contentWrapper,
              // contentWrapperStyle && contentWrapperStyle,
            ])}
          >
            <View
              style={StyleSheet.flatten([
                styles.iconContainerStyle,
                iconContainerStyle,
              ])}
            >
              <Icon
                name={
                  type === "email"
                    ? "envelope"
                    : type === "phone"
                    ? "phone"
                    : socialLinks[i].type
                }
                type={"simple-line-icon"}
                size={socialLinks[i].type === "social-youtube" ? 30 : 25}
                style={StyleSheet.flatten([styles.socialIconStyle])}
                onPress={() => {
                  if (type === "email") {
                    Linking.openURL(`mailto:${socialLinks[i].url}`);
                  } else if(type === "phone"){
                    Linking.openURL(`tel:${socialLinks[i].url}`)
                  } else {
                    _openLink(socialLinks[i].url);
                  }
                }}
              />
            </View>
          </View>
        );
      }
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: 'space-around',
            alignItems: "center",
          }}
        >
          {data}
        </div>
      );
    }
  };

  return (
    <ScrollView
      contentContainerStyle={StyleSheet.flatten([
        styles.container,
        containerStyle,
      ])}
    >
      {/* <GridV align={"middle"} justify={"start"} width={ScreenWidth * 0.9}> */}
      {_renderTitle()}
      <div>{_renderAbout()}</div>

      {_renderList()}
      {_renderSocialIcons()}
      {/* </GridV> */}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 60,
  },
  aboutInfoContainerStyle: {
    width: "100%",
  },
  titleStyle: {
    letterSpacing: 1,
    fontSize: 14,
    color: "#343a3b",
    paddingVertical: 5,
  },
  aboutInfoStyle: {
    textAlign: "left",
    fontSize: 12,
    padding: 10,
    color: "gray",
    lineHeight: 2,
  },
  highlightStyle: {
    fontWeight: "bold",
  },
  contentWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    // margin: 10,
    marginVertical: 10,
    marginHorizontal: 5,
    width: "100%",
    display: "flex",
  },
  labelStyle: {
    fontSize: 12,
    color: "#404040",
  },
  valueStyle: {
    fontSize: 13,
    fontWeight: "400",
    color: "black",
    paddingRight: 8,
  },
  socialIconContainer: {
    alignSelf: "center",
  },
  socialIconStyle: {
    paddingRight: 10,
  },
  iconContainerStyle: {
    alignItems: "center",
    justifyContent: "space-around",
    flexDirection: "row",
  },
});

DetailInfoV.defaultProps = {} as Partial<IDetailInfoV>;
